import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { DockListEnableItem, DockListInitialState, DockListLoad } from './dock-list.actions';
import { LIST_SERVER_PAGINATION_INITIAL_STATE, ListServerPaginationStateModel } from '../../../../../common/models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { DockListService } from './dock-list.service';
import { DockBrief } from '../models';
import { DockItemFacade } from '../item';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export type DockListStateModel = ListServerPaginationStateModel<DockBrief>;

const initialState: DockListStateModel = LIST_SERVER_PAGINATION_INITIAL_STATE;

@State<DockListStateModel>({
  name: 'dockList',
  defaults: initialState,
})
@Injectable()
export class DockListState {
  constructor(
    private dockListService: DockListService,
    private hubItemFacade: DockItemFacade,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: DockListStateModel): DockListStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: DockListStateModel): boolean {
    return state.loaded;
  }

  @Action(DockListLoad)
  getDocks(
    ctx: StateContext<DockListStateModel>,
    { payload }: DockListLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dockListService.getDockList(payload).pipe(
      take(1),
      map((list: DockBrief[]) => {
        ctx.patchState({ ...list, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DockListEnableItem)
  enableCompartmentOverview(
    ctx: StateContext<DockListStateModel>,
    { payload }: DockListEnableItem,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.dockListService.enableDock(payload).pipe(
      take(1),
      map(() => {
        const { hubId, compartmentId } = payload;
        this.notificationService.success('api.dock.success.update');
        ctx.dispatch(
          new DockListLoad({
            hubId,
            compartmentId,
            searchCriteria: null,
          }),
        );
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(DockListInitialState)
  setInitialState(ctx: StateContext<DockListStateModel>): void {
    ctx.setState(initialState);
  }
}
